import { useQuery } from '@tanstack/react-query';
import { get } from '../api/client';
import { isMissing, useMapRecord, useMaps } from '../api/queries';
import type { AutoChargeEvent, AutoChargeSettings, ChargerState, RobotStatus } from '../api/types';
import { parseChargers } from './chargerState';
import { t } from '../i18n';
import { useLive } from '../live/store';

export const AUTO_CHARGE_DEFAULTS: AutoChargeSettings = { enabled: true, lowPercent: 20, resumePercent: 80, criticalPercent: 10, resumeTask: true, quietHours: null };

export const settingsKey = (serial: string) => ['robot-settings', serial] as const;

/** GET /api/robots/:serial/settings → `autoCharge` merged over the defaults. */
export function useRobotSettings(serial: string) {
  return useQuery({
    queryKey: settingsKey(serial),
    queryFn: async () => {
      const r = await get<{ autoCharge?: Partial<AutoChargeSettings> | null } | null>(`/api/robots/${encodeURIComponent(serial)}/settings`);
      return { autoCharge: { ...AUTO_CHARGE_DEFAULTS, ...(r?.autoCharge ?? {}) } };
    },
    retry: (n, e) => !isMissing(e) && n < 2,
    staleTime: 30_000,
  });
}

export const chargersKey = (mapId: string) => ['map-chargers', mapId] as const;

/**
 * Occupancy of a map's chargers: the live /ws/ui "chargers" push wins, else GET /api/maps/:id/chargers
 * (404 → unknown). Returns pointId → state.
 */
export function useChargerOccupancy(mapId: string | undefined) {
  const live = useLive((s) => (mapId ? s.chargers[mapId] : undefined));
  const q = useQuery({
    queryKey: chargersKey(mapId ?? ''),
    queryFn: async () => {
      const list = parseChargers(await get<unknown>(`/api/maps/${mapId}/chargers`));
      if (mapId) useLive.getState().setChargers(mapId, list);
      return list;
    },
    enabled: Boolean(mapId),
    retry: (n, e) => !isMissing(e) && n < 2,
    refetchInterval: 30_000,
  });
  const list = live ?? q.data ?? [];
  const byId: Record<string, ChargerState> = {};
  for (const c of list) byId[c.pointId] = c;
  return byId;
}

/** The ⚡ charger points of a map (by name, as robots report `currentMap`) with their occupancy. */
export function useChargers(mapName: string | null | undefined) {
  const maps = useMaps();
  const mapId = mapName ? maps.data?.find((m) => m.name === mapName)?.id : undefined;
  const rec = useMapRecord(mapId);
  const occ = useChargerOccupancy(mapId);
  const points = (rec.data?.points ?? []).filter((p) => p.kind === 'charger');
  const chargers = points.map((p) => ({ point: p, occ: occ[p.id] ?? ({ pointId: p.id, state: 'unknown' } as ChargerState) }));
  return { mapId, chargers, known: Boolean(rec.data) };
}

/** Tooltip for "Go to charger": dock picks the nearest free ⚡ point when the current map has any. */
export function useDockTitle(mapName: string | null | undefined) {
  const { chargers, known } = useChargers(mapName);
  if (!known) return t('charge.dockTitle');
  if (chargers.length === 1) return t('charge.dockTitleCharger', { name: chargers[0]!.point.name });
  return chargers.length ? t('charge.dockTitleMany', { n: chargers.length }) : t('charge.dockTitleNoCharger');
}

/** Label for a charger's occupancy («свободна» / «занята: Робот X» / «зарезервирована»). */
export function occLabel(c: ChargerState, robotName: (serial: string) => string) {
  if (c.state === 'free') return t('charge.occ.free');
  if (c.state === 'occupied') return c.robot || c.robotName ? t('charge.occ.occupiedBy', { name: c.robotName ?? robotName(c.robot!) }) : t('charge.occ.occupied');
  if (c.state === 'reserved') return c.robot || c.robotName ? t('charge.occ.reservedBy', { name: c.robotName ?? robotName(c.robot!) }) : t('charge.occ.reserved');
  return t('charge.occ.unknown');
}

export type ChargeView = 'going' | 'charging' | 'failed' | null;

/** status.autoCharge.state (server truth), else derived from the latest autoCharge event. */
export function chargeViewOf(status: RobotStatus | null | undefined, ev: (AutoChargeEvent & { t: number }) | undefined): ChargeView {
  const s = status?.autoCharge?.state;
  if (s === 'going' || s === 'charging' || s === 'failed') return s;
  if (s === 'idle') return null;
  if (!ev) return null;
  if (ev.phase === 'low' || ev.phase === 'going') return 'going';
  if (ev.phase === 'docked') return 'charging';
  if (ev.phase === 'failed') return 'failed';
  return null;
}

export function useChargeView(serial: string): ChargeView {
  const status = useLive((s) => s.robots[serial]?.status);
  const ev = useLive((s) => s.autoCharge[serial]);
  return chargeViewOf(status, ev);
}
